import { supabase, supabaseWithoutCSRF } from "../supabaseClient";
import {
  AVERAGE_CATALOG_OPEN_REVIEW_MESSAGE,
  AverageCatalogInUseError,
  averageCatalogInUseMessage,
  isAverageCatalogOpenReviewError,
  tournamentNamesBlockingCatalogDelete,
  tournamentNamesFromCatalogLinks,
} from "./averageCatalogs.usage";

export {
  AVERAGE_CATALOG_OPEN_REVIEW_MESSAGE,
  AverageCatalogInUseError,
  averageCatalogInUseMessage,
  isAverageCatalogOpenReviewError,
  tournamentNamesBlockingCatalogDelete,
  tournamentNamesFromCatalogLinks,
};

export const LEAGUE_SHEET_BUCKET = "league-sheet-imports";
export const DEFAULT_MIN_GAMES_FOR_AVERAGE = 21;
export const MAX_MIN_GAMES_FOR_AVERAGE = 400;
export const MAX_STORED_FILE_BYTES = 20 * 1024 * 1024;
export const ALLOWED_LEAGUE_SHEET_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
];

export const chooseLeagueSheetFiles = (fileList) => {
  const accepted = [];
  const rejected = [];
  Array.from(fileList || []).forEach((nextFile) => {
    if (!ALLOWED_LEAGUE_SHEET_TYPES.includes(nextFile.type)) {
      rejected.push(`${nextFile.name} is not a PDF, PNG, or JPG.`);
      return;
    }
    if (nextFile.size > MAX_STORED_FILE_BYTES) {
      rejected.push(`${nextFile.name} is over 20MB.`);
      return;
    }
    accepted.push(nextFile);
  });
  return { accepted, rejected };
};

export const sanitizeLeagueSheetFileName = (fileName) =>
  (fileName || "league-sheet").replace(/[^a-zA-Z0-9.-]/g, "_").slice(0, 100) ||
  "league-sheet";

export const parseMinGamesForAverage = (value) => {
  const parsed = Number.parseInt(String(value), 10);
  if (
    !Number.isInteger(parsed) ||
    parsed < 1 ||
    parsed > MAX_MIN_GAMES_FOR_AVERAGE
  ) {
    return null;
  }
  return parsed;
};

export const averageMeetsMinimum = (games, minGamesForAverage) =>
  games != null && Number(games) >= minGamesForAverage;

export const minGamesForAverageReason = (games, minGamesForAverage) => {
  if (games == null) return "Games not listed";
  if (Number(games) < minGamesForAverage) {
    return `${games} games, minimum ${minGamesForAverage}`;
  }
  return null;
};

export const GAMES_CALCULATED_WARNING =
  "Games was calculated from pins and average";

export const NO_BOWLERS_ERROR_CODE = "no_bowlers";
export const NO_BOWLERS_MESSAGE =
  "No bowler averages were found on this sheet. Upload a league sheet that lists each bowler with an average and games.";

export const sheetFoundNoAverages = (row, averageRows = []) => {
  if (!row) return false;
  if (row.errorCode === NO_BOWLERS_ERROR_CODE) return true;
  if (row.status !== "completed") return false;
  return !averageRows.some((average) => average.importID === row.id);
};

export const sheetNoticeMessage = (row, averageRows = []) => {
  if (row?.errorMessage) return row.errorMessage;
  if (sheetFoundNoAverages(row, averageRows)) return NO_BOWLERS_MESSAGE;
  return "";
};

export const extractionFoundNoBowlers = (payload) =>
  !(payload?.extraction?.bowlers || []).length;

export const extractionNoBowlersMessage = (payload) =>
  (payload?.extraction?.warnings || []).find((message) =>
    String(message || "").trim()
  ) || NO_BOWLERS_MESSAGE;

export const gamesWereCalculated = (warnings) =>
  (warnings || []).some(
    (warning) => String(warning) === GAMES_CALCULATED_WARNING
  );

export const CATALOG_COLUMNS =
  "id, organizationID, name, asOfDate, createdAt, updatedAt";

export const listAverageCatalogs = async (organizationID) => {
  const { data, error } = await supabase
    .from("averageCatalogs")
    .select(CATALOG_COLUMNS)
    .eq("organizationID", organizationID)
    .order("createdAt", { ascending: false });

  if (error) throw error;
  return data || [];
};

export const getAverageCatalog = async (catalogID, organizationID) => {
  const { data, error } = await supabase
    .from("averageCatalogs")
    .select(CATALOG_COLUMNS)
    .eq("id", catalogID)
    .eq("organizationID", organizationID)
    .maybeSingle();

  if (error) throw error;
  return data;
};

export const createAverageCatalog = async ({
  organizationID,
  name,
  asOfDate,
  createdBy,
}) => {
  const { data, error } = await supabase
    .from("averageCatalogs")
    .insert({
      organizationID,
      name: name.trim(),
      asOfDate: asOfDate || null,
      createdBy,
      updatedAt: new Date().toISOString(),
    })
    .select(CATALOG_COLUMNS)
    .single();

  if (error) throw error;
  return data;
};

export const updateAverageCatalog = async (catalogID, organizationID, updates) => {
  const { data, error } = await supabase
    .from("averageCatalogs")
    .update({
      ...updates,
      updatedAt: new Date().toISOString(),
    })
    .eq("id", catalogID)
    .eq("organizationID", organizationID)
    .select(CATALOG_COLUMNS)
    .single();

  if (error) throw error;
  return data;
};

export const listTournamentsBlockingAverageCatalogDelete = async (
  catalogID
) => {
  if (!catalogID) return [];
  const { data: links, error: linkError } = await supabase
    .from("tournamentAverageCatalogs")
    .select("tournamentID")
    .eq("catalogID", catalogID);
  if (linkError) throw linkError;
  const ids = (links || []).map((row) => row.tournamentID).filter(Boolean);
  if (!ids.length) return [];

  const { data: tournaments, error: tournamentError } = await supabase
    .from("tournaments")
    .select("id, name, isDeleted")
    .in("id", ids);
  if (tournamentError) throw tournamentError;

  const live = (tournaments || []).filter((row) => !row.isDeleted);
  const liveIds = live.map((row) => row.id);
  if (!liveIds.length) return [];

  const { data: teams, error: teamsError } = await supabase
    .from("teams")
    .select("id, teamassignments(bowlerID), squads!inner(tournamentID)")
    .in("squads.tournamentID", liveIds)
    .eq("isDeleted", false);
  if (teamsError) throw teamsError;

  const { data: averages, error: averagesError } = await supabase
    .from("verificationStates")
    .select("tournamentId, entityId, isVerified")
    .eq("entityType", "averages")
    .in("tournamentId", liveIds);
  if (averagesError) throw averagesError;

  return tournamentNamesBlockingCatalogDelete({
    tournaments: live,
    teams: teams || [],
    averages: averages || [],
  });
};

export const deleteAverageCatalog = async (catalogID, organizationID) => {
  if (!catalogID || !organizationID) {
    throw new Error("Could not delete this catalog.");
  }

  const names = await listTournamentsBlockingAverageCatalogDelete(catalogID);
  if (names.length) {
    throw new AverageCatalogInUseError(names);
  }

  const { error: unlinkError } = await supabase
    .from("tournamentAverageCatalogs")
    .delete()
    .eq("catalogID", catalogID);
  if (unlinkError) {
    if (isAverageCatalogOpenReviewError(unlinkError)) {
      throw new AverageCatalogInUseError([]);
    }
    throw unlinkError;
  }

  const imports = await listLeagueSheetImports(catalogID, organizationID);
  const paths = imports.map((row) => row.storagePath).filter(Boolean);
  if (paths.length) {
    const { error: storageError } = await supabase.storage
      .from(LEAGUE_SHEET_BUCKET)
      .remove(paths);
    if (
      storageError &&
      !/not found|object not found/i.test(storageError.message || "")
    ) {
      throw storageError;
    }
  }

  const { error } = await supabase
    .from("averageCatalogs")
    .delete()
    .eq("id", catalogID)
    .eq("organizationID", organizationID);
  if (error) {
    if (error.code === "23503" || isAverageCatalogOpenReviewError(error)) {
      throw new AverageCatalogInUseError([]);
    }
    throw error;
  }
};

export const listLeagueSheetImports = async (catalogID, organizationID) => {
  const { data, error } = await supabase
    .from("leagueSheetImports")
    .select(
      "id, catalogID, organizationID, leagueName, storagePath, fileName, fileSize, mimeType, pageCount, status, errorCode, errorMessage, createdAt, updatedAt"
    )
    .eq("catalogID", catalogID)
    .eq("organizationID", organizationID)
    .order("createdAt", { ascending: false });

  if (error) throw error;
  return data || [];
};

export const listCatalogExtractedAverages = async (
  catalogID,
  organizationID
) => {
  const imports = await listLeagueSheetImports(catalogID, organizationID);
  const completed = imports.filter((row) => row.status === "completed");
  if (completed.length === 0) return [];

  const { data, error } = await supabase
    .from("extractedBowlers")
    .select(
      `
      id,
      importID,
      extractedName,
      extractedUsbcId,
      sourcePage,
      boundingBox,
      sortIndex,
      extractedAverages (
        id,
        average,
        games,
        pins,
        label,
        warnings,
        sourcePage,
        boundingBox,
        sortIndex
      )
    `
    )
    .eq("organizationID", organizationID)
    .in(
      "importID",
      completed.map((row) => row.id)
    )
    .order("sortIndex", { ascending: true });

  if (error) throw error;

  const importById = new Map(completed.map((row) => [row.id, row]));

  return (data || []).flatMap((bowler) => {
    const sheet = importById.get(bowler.importID);
    const averages = [...(bowler.extractedAverages || [])].sort(
      (left, right) => (left.sortIndex || 0) - (right.sortIndex || 0)
    );
    return averages.map((average) => ({
      id: `${bowler.id}-${average.id}`,
      extractedBowlerID: bowler.id,
      extractedAverageID: average.id,
      sortIndex: bowler.sortIndex,
      averageSortIndex: average.sortIndex,
      name: bowler.extractedName,
      usbcId: bowler.extractedUsbcId,
      average: average.average,
      games: average.games,
      pins: average.pins,
      gamesCalculated: gamesWereCalculated(average.warnings),
      source:
        average.label ||
        sheet?.leagueName ||
        sheet?.fileName ||
        "Not on sheet",
      importID: bowler.importID,
      leagueName: sheet?.leagueName || null,
      fileName: sheet?.fileName || null,
      storagePath: sheet?.storagePath || null,
      mimeType: sheet?.mimeType || null,
      sourcePage: average.sourcePage ?? bowler.sourcePage ?? null,
      boundingBox: average.boundingBox || bowler.boundingBox || null,
    }));
  });
};

export const listExtractedBowlers = async (importID, organizationID) => {
  const { data, error } = await supabase
    .from("extractedBowlers")
    .select(
      `
      id,
      extractedName,
      extractedUsbcId,
      warnings,
      sortIndex,
      extractedAverages (
        id,
        average,
        games,
        pins,
        label,
        warnings,
        sortIndex
      )
    `
    )
    .eq("importID", importID)
    .eq("organizationID", organizationID)
    .order("sortIndex", { ascending: true });

  if (error) throw error;
  return (data || []).map((bowler) => ({
    ...bowler,
    extractedAverages: [...(bowler.extractedAverages || [])].sort(
      (left, right) => (left.sortIndex || 0) - (right.sortIndex || 0)
    ),
  }));
};

export const createLeagueSheetImport = async ({
  catalogID,
  organizationID,
  leagueName,
  file,
  createdBy,
}) => {
  const importID = crypto.randomUUID();
  const fileName = sanitizeLeagueSheetFileName(file.name);
  const storagePath = `${organizationID}/${catalogID}/${importID}/${fileName}`;

  const { data, error } = await supabase
    .from("leagueSheetImports")
    .insert({
      id: importID,
      catalogID,
      organizationID,
      leagueName: leagueName?.trim() || null,
      storagePath,
      fileName,
      fileSize: file.size,
      mimeType: file.type,
      status: "uploaded",
      createdBy,
      updatedAt: new Date().toISOString(),
    })
    .select(
      "id, catalogID, organizationID, leagueName, storagePath, fileName, status"
    )
    .single();

  if (error) throw error;

  const { error: uploadError } = await supabase.storage
    .from(LEAGUE_SHEET_BUCKET)
    .upload(storagePath, file, {
      cacheControl: "3600",
      upsert: false,
      contentType: file.type,
    });

  if (uploadError) {
    await supabase
      .from("leagueSheetImports")
      .update({
        status: "failed",
        errorCode: "invalid_file",
        errorMessage: uploadError.message,
        updatedAt: new Date().toISOString(),
      })
      .eq("id", importID);
    throw uploadError;
  }

  return data;
};

export const updateLeagueSheetImportLeagueName = async ({
  importID,
  organizationID,
  leagueName,
}) => {
  const { data, error } = await supabase
    .from("leagueSheetImports")
    .update({
      leagueName: leagueName?.trim() || null,
      updatedAt: new Date().toISOString(),
    })
    .eq("id", importID)
    .eq("organizationID", organizationID)
    .select(
      "id, catalogID, organizationID, leagueName, storagePath, fileName, status"
    )
    .single();

  if (error) throw error;
  return data;
};

export const deleteLeagueSheetImport = async (importRow, organizationID) => {
  if (importRow.storagePath) {
    const { error: storageError } = await supabase.storage
      .from(LEAGUE_SHEET_BUCKET)
      .remove([importRow.storagePath]);
    if (
      storageError &&
      !/not found|object not found/i.test(storageError.message || "")
    ) {
      throw storageError;
    }
  }

  const { error } = await supabase
    .from("leagueSheetImports")
    .delete()
    .eq("id", importRow.id)
    .eq("organizationID", organizationID);

  if (error) throw error;
};

export const createLeagueSheetSignedUrl = async (
  storagePath,
  expiresIn = 300
) => {
  if (!storagePath) {
    throw new Error("This league sheet file is not stored.");
  }
  const { data, error } = await supabase.storage
    .from(LEAGUE_SHEET_BUCKET)
    .createSignedUrl(storagePath, expiresIn);
  if (error) throw error;
  if (!data?.signedUrl) {
    throw new Error("Could not open this league sheet.");
  }
  return data.signedUrl;
};

export const processLeagueSheetImport = async (
  importID,
  organizationID,
  tournamentID = null
) => {
  const {
    data: { session },
  } = await supabaseWithoutCSRF.auth.getSession();
  if (!session?.access_token) {
    throw new Error("You need to be signed in.");
  }

  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
  const body = { importID, organizationID };
  // Org Average catalogs omit tournamentID so extract requires paid/grandfather.
  // Tournament Setup passes the tournament id for the first-tournament trial.
  if (tournamentID) body.tournamentID = tournamentID;
  const response = await fetch(
    `${supabaseUrl}/functions/v1/process-league-sheet-poc`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        apikey: supabaseAnonKey,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    }
  );

  const payload = await response.json();
  if (!response.ok) {
    throw new Error(payload.error || "Extraction failed");
  }
  return payload;
};

export const extractLeagueSheetFiles = async ({
  catalogID,
  organizationID,
  leagueName,
  files,
  createdBy,
  tournamentID = null,
  onProgress,
  onFileDone,
}) => {
  const queue = [...(files || [])];
  const total = queue.length;
  const failures = [];
  const emptySheets = [];
  if (total === 0) {
    return { failures, emptySheets };
  }
  let done = 0;
  const extractOne = async (file) => {
    try {
      const importRow = await createLeagueSheetImport({
        catalogID,
        organizationID,
        leagueName,
        file,
        createdBy,
      });
      const payload = await processLeagueSheetImport(
        importRow.id,
        organizationID,
        tournamentID
      );
      if (extractionFoundNoBowlers(payload)) {
        emptySheets.push(
          `${file.name}: ${extractionNoBowlersMessage(payload)}`
        );
      }
    } catch (err) {
      console.error(err);
      failures.push(
        `${file.name}: ${err.message || "Could not extract this league sheet."}`
      );
    } finally {
      done += 1;
      onProgress?.({ done: Math.min(done, total), total });
      await onFileDone?.();
    }
  };
  const workers = Array.from({ length: Math.min(2, queue.length) }, async () => {
    while (queue.length) {
      const nextFile = queue.shift();
      if (nextFile) await extractOne(nextFile);
    }
  });
  await Promise.all(workers);
  return { failures, emptySheets };
};

export const listCatalogExtractedPeople = async (catalogID, organizationID) => {
  const imports = await listLeagueSheetImports(catalogID, organizationID);
  const completed = imports.filter((row) => row.status === "completed");
  if (completed.length === 0) return [];

  const importById = new Map(completed.map((row) => [row.id, row]));
  const { data, error } = await supabase
    .from("extractedBowlers")
    .select(
      `
      id,
      importID,
      extractedName,
      extractedUsbcId,
      sourcePage,
      boundingBox,
      sortIndex,
      extractedAverages (
        id,
        average,
        games,
        pins,
        label,
        warnings,
        sourcePage,
        boundingBox,
        sortIndex
      )
    `
    )
    .eq("organizationID", organizationID)
    .in(
      "importID",
      completed.map((row) => row.id)
    )
    .order("sortIndex", { ascending: true });

  if (error) throw error;

  return (data || []).map((bowler) => {
    const sheet = importById.get(bowler.importID);
    return {
      id: bowler.id,
      name: bowler.extractedName,
      usbcId: bowler.extractedUsbcId,
      importID: bowler.importID,
      leagueName: sheet?.leagueName || null,
      fileName: sheet?.fileName || null,
      storagePath: sheet?.storagePath || null,
      mimeType: sheet?.mimeType || null,
      averages: [...(bowler.extractedAverages || [])]
        .sort((left, right) => (left.sortIndex || 0) - (right.sortIndex || 0))
        .map((average) => ({
          ...average,
          source:
            average.label ||
            sheet?.leagueName ||
            sheet?.fileName ||
            "Not on sheet",
          storagePath: sheet?.storagePath || null,
          mimeType: sheet?.mimeType || null,
          fileName: sheet?.fileName || null,
          leagueName: sheet?.leagueName || null,
        })),
    };
  });
};

export const getTournamentAverageCatalog = async (tournamentID) => {
  const { data, error } = await supabase
    .from("tournamentAverageCatalogs")
    .select(
      `
      tournamentID,
      catalogID,
      minGamesForAverage,
      linkedAt,
      averageCatalogs (
        id,
        name,
        asOfDate,
        organizationID
      )
    `
    )
    .eq("tournamentID", tournamentID)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;
  return {
    tournamentID: data.tournamentID,
    catalogID: data.catalogID,
    minGamesForAverage:
      parseMinGamesForAverage(data.minGamesForAverage) ||
      DEFAULT_MIN_GAMES_FOR_AVERAGE,
    linkedAt: data.linkedAt,
    catalog: data.averageCatalogs || null,
  };
};

export const setTournamentAverageCatalog = async ({
  tournamentID,
  catalogID,
  minGamesForAverage,
  linkedBy,
}) => {
  const parsedMinGames =
    parseMinGamesForAverage(minGamesForAverage) ||
    DEFAULT_MIN_GAMES_FOR_AVERAGE;
  const { data, error } = await supabase
    .from("tournamentAverageCatalogs")
    .upsert(
      {
        tournamentID,
        catalogID,
        minGamesForAverage: parsedMinGames,
        linkedAt: new Date().toISOString(),
        linkedBy: linkedBy || null,
      },
      { onConflict: "tournamentID" }
    )
    .select("tournamentID, catalogID, minGamesForAverage, linkedAt")
    .single();

  if (error) {
    if (isAverageCatalogOpenReviewError(error)) {
      throw new AverageCatalogInUseError([]);
    }
    throw error;
  }
  return data;
};

export const clearTournamentAverageCatalog = async (tournamentID) => {
  const { error } = await supabase
    .from("tournamentAverageCatalogs")
    .delete()
    .eq("tournamentID", tournamentID);
  if (error) {
    if (isAverageCatalogOpenReviewError(error)) {
      throw new AverageCatalogInUseError([]);
    }
    throw error;
  }
};

export const updateExtractedBowlerName = async ({
  extractedBowlerID,
  organizationID,
  name,
}) => {
  const extractedName = String(name || "").trim();
  if (!extractedName) {
    throw new Error("Name is required.");
  }
  const { error } = await supabase
    .from("extractedBowlers")
    .update({ extractedName })
    .eq("id", extractedBowlerID)
    .eq("organizationID", organizationID);
  if (error) throw error;
};

export const updateExtractedAverageValues = async ({
  extractedAverageID,
  organizationID,
  average,
  games,
  pins,
}) => {
  const { error } = await supabase
    .from("extractedAverages")
    .update({
      average,
      games,
      pins,
    })
    .eq("id", extractedAverageID)
    .eq("organizationID", organizationID);
  if (error) throw error;
};

